/**
 * Simulated writes.
 *
 * A tour that demonstrates Create, Save, Approve or Delete must show the
 * outcome without touching the tenant's data. Each handler here records the
 * change in the demo state — so the list the user goes back to reflects what
 * they just did — and answers with the success envelope the page expects.
 * Nothing is sent.
 */

import { TOUR_WORKFLOW_STATUS } from '../data/tour-sample-approvals';
import {
  addTourRecord,
  patchTourRecord,
  recordTourMutation,
  removeTourRecord,
} from '../state/tour-demo-state';

import { TourRequestContext } from './tour-data-source';

/** Note the write for troubleshooting, then hand back a success body. */
const simulated = (
  ctx: TourRequestContext,
  message: string,
  data: unknown = null
) => {
  recordTourMutation({
    method: ctx.method,
    url: ctx.path,
    body: ctx.body,
    at: Date.now(),
  });
  return { ...(data && typeof data === 'object' ? data : {}), message };
};

/** A simulated create: the new row is prepended to the sample collection. */
export const simulateCreate =
  (collection: string, idKey: string, message = 'Created successfully.') =>
  (ctx: TourRequestContext) => {
    const body = (ctx.body ?? {}) as Record<string, unknown>;
    // Multipart bodies (file uploads) arrive as FormData and carry no fields to
    // fold into the list, so only a plain JSON body becomes a new row.
    if (body && typeof body === 'object' && !(ctx.body instanceof FormData)) {
      const id = `TOUR-NEW-${Date.now()}`;
      addTourRecord(collection, {
        ...body,
        [idKey]: body[idKey] ?? id,
        id: body.id ?? id,
        status: body.status ?? 'Pending Checker Approval',
        statusName: body.statusName ?? 'Pending Checker Approval',
        createdBy: 'You (tour)',
        createdAt: new Date().toISOString(),
      });
    }
    return simulated(ctx, message);
  };

/** A simulated update: the edited fields are patched onto the sample row. */
export const simulateUpdate =
  (collection: string, idParam: string, message = 'Updated successfully.') =>
  (ctx: TourRequestContext) => {
    const id = ctx.params[idParam];
    if (
      id &&
      ctx.body &&
      typeof ctx.body === 'object' &&
      !(ctx.body instanceof FormData)
    ) {
      patchTourRecord(collection, id, ctx.body as Record<string, unknown>);
    }
    return simulated(ctx, message);
  };

/** A simulated delete: the row disappears from the sample collection. */
export const simulateDelete =
  (collection: string, idParam: string, message = 'Deleted successfully.') =>
  (ctx: TourRequestContext) => {
    const id = ctx.params[idParam];
    if (id) removeTourRecord(collection, id);
    return simulated(ctx, message);
  };

/** How a checker decision finds and labels the record it settles. */
interface CheckerActionOptions {
  /**
   * The collection's key for the record a transaction id names, when the
   * collection is not keyed by `txnId` itself (e.g. policies by `policyId`).
   */
  recordIdFor?: (txnId: string) => string | number | undefined;
  /** A further status-name field the module's lists read (and filter on). */
  statusField?: string;
}

/**
 * A simulated maker-checker decision: the record's status moves to Approved or
 * Rejected in the demo state, so the dashboard count and the row both settle.
 */
export const simulateCheckerAction =
  (collection: string, options: CheckerActionOptions = {}) =>
  (ctx: TourRequestContext) => {
    const body = (ctx.body ?? {}) as { txnId?: string; action?: string };
    // The checker endpoints encode the decision as a numeric action code; 7 is
    // Approve and anything else in the tour's reach is a rejection.
    const approved = String(body.action) === '7';
    const recordId = body.txnId
      ? (options.recordIdFor?.(body.txnId) ?? body.txnId)
      : undefined;
    if (recordId !== undefined) {
      const statusName = approved ? 'Approved' : 'Rejected';
      patchTourRecord(collection, recordId, {
        // The id is what the Maker / Checker buckets filter on; without it an
        // actioned record stays under Pending Checker Approval.
        workflowStatusId: approved
          ? TOUR_WORKFLOW_STATUS.approved
          : TOUR_WORKFLOW_STATUS.rejected,
        txnStatus: statusName,
        status: statusName,
        statusName,
        ...(options.statusField && { [options.statusField]: statusName }),
        lastChecker: 'You (tour)',
        lastCheckerDatetime: new Date().toISOString(),
      });
    }
    return simulated(
      ctx,
      approved ? 'Record approved successfully.' : 'Record rejected.'
    );
  };

/** A simulated status change (activate / deactivate a record). */
export const simulateStatusChange =
  (collection: string, idParam: string) => (ctx: TourRequestContext) => {
    const id = ctx.params[idParam];
    const body = (ctx.body ?? {}) as Record<string, unknown>;
    if (id) {
      patchTourRecord(collection, id, {
        status: body.status ?? body.statusName ?? 'Inactive',
        statusName: body.statusName ?? body.status ?? 'Inactive',
      });
    }
    return simulated(ctx, 'Status updated successfully.');
  };

/** A write with no list to update — the tour only needs the success state. */
export const simulateAcknowledge =
  (message: string) => (ctx: TourRequestContext) =>
    simulated(ctx, message);
